// src/pages/Portfolio.tsx: the allocator's private portfolio tracker.
//
// Everything here reads allocator_investments, whose only RLS policy scopes
// rows to allocator_profile_id = auth.uid(). No manager or admin policy exists,
// so a manager cannot see who holds a position in them.
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import { C, fmtDate } from '../lib/applications';
import {
  Investment, PortfolioSummary, calculatePortfolioYTD,
  formatReturn, formatAUM, totalLabel,
} from '../lib/portfolioCalc';
import AddInvestmentModal from '../components/AddInvestmentModal';

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const btnGold: React.CSSProperties = {
  background: C.gold, color: C.navy, border: 'none', borderRadius: '3px',
  padding: '10px 20px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui',
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '9px 12px', fontSize: '9.5px',
  color: 'rgba(255,255,255,0.7)', fontWeight: 700,
  letterSpacing: '0.6px', textTransform: 'uppercase', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '11px 12px', fontSize: '12.5px', color: C.textSec, verticalAlign: 'top' };

export default function Portfolio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, firm, loading: profileLoading } = useProfile();

  const [rows, setRows]         = useState<Investment[]>([]);
  const [summary, setSummary]   = useState<PortfolioSummary | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modalOpen, setModal]   = useState(false);
  const [editing, setEditing]   = useState<Investment | null>(null);
  const [savingValue, setSavingValue] = useState<string | null>(null);

  const isAllocator = profile?.role === 'allocator';

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true); setError('');
    const { data, error: err } = await supabase.from('allocator_investments')
      .select('*').eq('is_active', true).order('investment_date', { ascending: false });
    if (err) { setError(err.message); setLoading(false); return; }
    const list = (data as Investment[]) || [];
    setRows(list);
    setSummary(await calculatePortfolioYTD(list));
    setLoading(false);
  }, [user]);

  useEffect(() => { if (isAllocator) load(); }, [isAllocator, load]);

  const remove = async (inv: Investment) => {
    if (!window.confirm(`Remove ${inv.fund_name} from your portfolio? This hides it, the record is kept.`)) return;
    const { error: err } = await supabase.from('allocator_investments')
      .update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', inv.id);
    if (err) { setError(err.message); return; }
    load();
  };

  // Current value is edited in place, saved on blur.
  const saveCurrentValue = async (inv: Investment, raw: string) => {
    const parsed = raw.trim() === '' ? null : parseFloat(raw);
    if (parsed !== null && Number.isNaN(parsed)) return;
    if (parsed === inv.current_value) return;
    setSavingValue(inv.id);
    const { error: err } = await supabase.from('allocator_investments')
      .update({ current_value: parsed, updated_at: new Date().toISOString() }).eq('id', inv.id);
    setSavingValue(null);
    if (err) { setError(err.message); return; }
    setRows(prev => prev.map(r => r.id === inv.id ? { ...r, current_value: parsed } : r));
  };

  if (profileLoading) return (
    <div style={{ padding: '48px 32px', color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading…</div>
  );
  if (!isAllocator) return <Navigate to="/dashboard" replace />;

  const managers = new Set(rows.map(r => r.manager_firm_id || r.manager_name)).size;
  const funds = new Set(rows.map(r => r.fund_id || `${r.manager_name}:${r.fund_name}`)).size;
  const coveragePct = summary && summary.totalInvested > 0
    ? Math.round((summary.coveredInvested / summary.totalInvested) * 100) : 0;

  const ytdColour = !summary?.ytdReturn ? C.muted
    : summary.ytdReturn > 0 ? C.success : summary.ytdReturn < 0 ? C.danger : C.navy;

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '22px' }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
            My Portfolio
          </h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
            Private portfolio tracker, visible only to you. Managers cannot see these positions.
          </p>
        </div>
        <button style={btnGold} onClick={() => { setEditing(null); setModal(true); }}>+ Add Investment</button>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '16px', fontSize: '12.5px', color: C.danger }}>{error}</div>
      )}

      {/* ── Summary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <SummaryCard label="Total AUM Invested" accent={C.navy}
          value={loading ? '—' : totalLabel(summary?.totalInvested || 0, summary?.currencies || [])}
          note={summary && summary.currencies.length > 1 ? 'Mixed currencies, not converted' : 'As entered'} />
        <SummaryCard label="YTD Return" accent={ytdColour}
          value={loading ? '—' : formatReturn(summary?.ytdReturn ?? null)}
          note={loading ? '' : summary?.ytdReturn == null ? 'No linked performance data' : `Weighted over ${coveragePct}% of the book`} />
        <SummaryCard label="Managers" accent={C.navy} value={loading ? '—' : String(managers)} note="Distinct managers" />
        <SummaryCard label="Funds" accent={C.navy} value={loading ? '—' : String(funds)} note="Positions held" />
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading your portfolio…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...card, padding: '52px 40px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: C.text, marginBottom: '8px' }}>
            No investments tracked yet
          </div>
          <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7, maxWidth: '420px', margin: '0 auto 22px' }}>
            Add your first investment to start tracking your portfolio performance.
          </p>
          <button style={btnGold} onClick={() => { setEditing(null); setModal(true); }}>+ Add Investment</button>
        </div>
      ) : (
        <div style={{ ...card, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1020px' }}>
            <thead>
              <tr style={{ background: C.navy }}>
                {['Manager', 'Fund', 'Invested', 'Date', 'Currency', 'YTD Return', 'Current Value', 'Notes', ''].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((inv, i) => {
                const ytd = inv.fund_id ? summary?.fundYtd[inv.fund_id] : undefined;
                return (
                  <tr key={inv.id} style={{ borderBottom: `1px solid ${C.borderLt}`, background: i % 2 === 0 ? C.white : C.surface }}>
                    <td style={{ ...td, color: C.text, fontWeight: 600 }}>
                      {inv.manager_firm_id ? (
                        <span onClick={() => navigate(`/alt-connect/firm/${inv.manager_firm_id}`)}
                          style={{ color: C.teal, cursor: 'pointer' }}>{inv.manager_name}</span>
                      ) : inv.manager_name}
                    </td>
                    <td style={td}>{inv.fund_name}</td>
                    <td style={{ ...td, fontFamily: '"Courier New", monospace', color: C.text }}>
                      {formatAUM(inv.amount_invested, inv.currency)}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(inv.investment_date)}</td>
                    <td style={td}>
                      <span style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '9.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.5px' }}>
                        {inv.currency}
                      </span>
                    </td>
                    <td style={{ ...td, fontFamily: '"Courier New", monospace', fontWeight: 700, whiteSpace: 'nowrap', color: ytd == null ? C.mutedLt : ytd > 0 ? C.success : ytd < 0 ? C.danger : C.muted }}>
                      {ytd == null ? '--' : formatReturn(ytd)}
                    </td>
                    <td style={td}>
                      <input
                        defaultValue={inv.current_value != null ? String(inv.current_value) : ''}
                        placeholder="—"
                        onBlur={e => saveCurrentValue(inv, e.target.value)}
                        style={{
                          width: '110px', background: C.white, border: `1px solid ${C.border}`,
                          borderRadius: '3px', padding: '5px 8px', fontSize: '12px',
                          fontFamily: '"Courier New", monospace', color: C.text, outline: 'none',
                          opacity: savingValue === inv.id ? 0.6 : 1,
                        }}
                      />
                    </td>
                    <td style={{ ...td, maxWidth: '180px' }}>
                      <span title={inv.notes || ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.muted }}>
                        {inv.notes || '—'}
                      </span>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => { setEditing(inv); setModal(true); }} title="Edit"
                        style={iconBtn}>✎</button>
                      <button onClick={() => remove(inv)} title="Remove"
                        style={{ ...iconBtn, color: C.danger }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && summary && summary.coveredInvested < summary.totalInvested && (
        <div style={{ fontSize: '11.5px', color: C.mutedLt, marginTop: '10px', lineHeight: 1.6 }}>
          YTD is measured over the {coveragePct}% of the portfolio held in funds listed on Altveno.
          Manually entered positions have no performance data and are excluded from the weighted figure.
        </div>
      )}

      {modalOpen && user && (
        <AddInvestmentModal
          allocatorProfileId={user.id}
          allocatorFirmId={firm?.id || null}
          existing={editing}
          onClose={() => { setModal(false); setEditing(null); }}
          onSaved={() => { setModal(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: '14px', color: C.muted, padding: '2px 6px', fontFamily: 'system-ui',
};

function SummaryCard({ label, value, note, accent }: {
  label: string; value: string; note?: string; accent: string;
}) {
  return (
    <div style={{ ...card, padding: '16px 18px', borderLeft: `3px solid ${accent}` }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: accent, lineHeight: 1, letterSpacing: '-0.5px', marginBottom: '6px' }}>{value}</div>
      <div style={{ fontSize: '11px', color: C.mutedLt }}>{note || ' '}</div>
    </div>
  );
}
